import { Component, EventEmitter, Input, Output } from '@angular/core';

import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

/**
 * Cascarón compartido para los diálogos de alta/edición de los módulos.
 *
 * Los formularios de Módulos, Usuarios, Roles, etc. se abrían como panel
 * inline arriba de la tabla: con formularios largos había que scrollear toda
 * la página para llegar a Guardar/Cancelar. Este cascarón estandariza el
 * reemplazo: título fijo arriba, acciones fijas abajo y SOLO el contenido
 * (lo proyectado con ng-content) scrollea.
 *
 * Uso, dentro de un componente abierto con MatDialog:
 *
 *   <app-form-dialog-shell
 *     [icon]="isEditing ? 'edit' : 'add_circle'"
 *     [heading]="isEditing ? 'Editar módulo' : 'Nuevo módulo'"
 *     [saving]="saving()"
 *     [saveLabel]="isEditing ? 'Guardar cambios' : 'Crear módulo'"
 *     [saveIcon]="isEditing ? 'save' : 'add'"
 *     formId="module-form"
 *     (cancelled)="close()">
 *       <form id="module-form" [formGroup]="form" (ngSubmit)="onSubmit()">…</form>
 *   </app-form-dialog-shell>
 *
 * El botón Guardar dispara el submit del formulario proyectado vía el
 * atributo HTML `form` (por eso el <form> necesita el mismo id que
 * `formId`); el diálogo dueño implementa onSubmit() y cierra con
 * `dialogRef.close(true)` para que la pantalla padre recargue su tabla.
 */
@Component({
  selector: 'app-form-dialog-shell',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule],
  templateUrl: './form-dialog-shell.component.html',
  styleUrl: './form-dialog-shell.component.scss',
})
export class FormDialogShellComponent {
  /** Ícono del encabezado (ej. 'edit' / 'add_circle'). */
  @Input() icon = 'edit';
  /** Título del diálogo. */
  @Input() heading = '';
  /** true mientras guarda: deshabilita botones y muestra spinner. */
  @Input() saving = false;
  /** Deshabilita el botón Guardar además de `saving` (ej. form inválido). */
  @Input() disabled = false;
  @Input() saveLabel = 'Guardar';
  @Input() saveIcon = 'save';
  @Input() cancelLabel = 'Cancelar';
  /** id del <form> proyectado; el botón Guardar le hace submit. */
  @Input({ required: true }) formId!: string;

  @Output() cancelled = new EventEmitter<void>();
}
